import React from 'react'
import { ComponentConfig } from '@puckeditor/core'
import { getPuckFileUrl, UploadedFile } from '../../fields/FileUploadField'
import type { ThemeCustomFields } from '../../theme-config'
import { withStyleFields } from '../StyleWrapper'
import { withLayout } from '../Layout'
import {
  BackgroundStyleProps,
  BorderStyleProps,
  applyStyleProps,
} from '../../fields/StyleFields'
import { Spacing } from '../../fields/SpacingField'
import * as styles from './Image.module.scss'

export type ImageProps = {
  src: UploadedFile | string
  alt: string
  width?: string
  height?: string
  maxWidth?: string
  maxHeight?: string
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
  /** @deprecated BR-5346 bespoke spacing — replaced by withLayout's layout.padding; still applied for old saves. */
  padding?: Spacing
  /** @deprecated BR-5346 bespoke spacing — replaced by withLayout's layout.margin; still applied for old saves. */
  margin?: Spacing
  background?: BackgroundStyleProps
  border?: BorderStyleProps
}

const cssSpacingVal = (v?: string) =>
  !v || v === '0' ? undefined : /^\d+(\.\d+)?$/.test(v) ? `${v}px` : v

/** Inline padding/margin — only the sides the user set. */
const buildSpacing = (
  padding?: Spacing,
  margin?: Spacing,
): React.CSSProperties => ({
  paddingTop: cssSpacingVal(padding?.top),
  paddingRight: cssSpacingVal(padding?.right),
  paddingBottom: cssSpacingVal(padding?.bottom),
  paddingLeft: cssSpacingVal(padding?.left),
  marginTop: cssSpacingVal(margin?.top),
  marginRight: cssSpacingVal(margin?.right),
  marginBottom: cssSpacingVal(margin?.bottom),
  marginLeft: cssSpacingVal(margin?.left),
})

// withLayout adds padding/margin/size on a wrapper (BR-5406), replacing the
// bespoke BR-5346 spacing fields; style fields stay on the inner
// withStyleFields so border-radius applies to the <img> itself.
export const PuckImage = withLayout(
  withStyleFields<ComponentConfig<{
    props: ImageProps
    fields: ThemeCustomFields
  }>>({
  label: 'Image',
  defaultProps: {
    src: '',
    alt: '',
    width: '',
    height: '',
    maxWidth: '100%',
    maxHeight: '',
    objectFit: 'cover',
  },
  fields: {
    src: { type: 'fileUpload', label: 'Image' },
    alt: { type: 'text', label: 'Alt Text' },
    width: { type: 'text', label: 'Width (e.g. 300px, 50%)' },
    height: { type: 'text', label: 'Height (e.g. 150px, auto)' },
    maxWidth: { type: 'text', label: 'Max Width (e.g. 300px)' },
    maxHeight: { type: 'text', label: 'Max Height (e.g. 150px)' },
    objectFit: {
      type: 'radio',
      label: 'Object Fit',
      options: [
        { label: 'Cover', value: 'cover' },
        { label: 'Contain', value: 'contain' },
        { label: 'Fill', value: 'fill' },
        { label: 'None', value: 'none' },
        { label: 'Scale Down', value: 'scale-down' },
      ],
    },
  },
  render: ({
    src,
    alt,
    width,
    height,
    maxWidth,
    maxHeight,
    objectFit,
    padding,
    margin,
    background,
    border,
  }) => {
    const url = typeof src === 'string' ? src : getPuckFileUrl(src)
    const styleProps = applyStyleProps({ background, border })
    const spacing = buildSpacing(padding, margin)

    if (!url) {
      return (
        <div
          className={styles.placeholder}
          style={{
            height: height || undefined,
            ...styleProps,
            ...spacing,
          }}
        >
          No image selected
        </div>
      )
    }

    return (
      <img
        src={url}
        alt={alt}
        style={{
          width: '100%',
          ...(height ? { height } : {}),
          maxWidth: '100%',
          display: 'block',
          ...(maxHeight || height
            ? { maxHeight, objectFit: objectFit || 'cover' }
            : {}),
          ...styleProps,
          ...spacing,
        }}
      />
    )
    },
  }),
  {
    disableStyleFields: true,
    wrapperStyle: p => ({
      ...(p.width ? { width: p.width } : {}),
      ...(p.maxWidth && p.maxWidth !== '100%' ? { maxWidth: p.maxWidth } : {}),
    }),
  },
)
